import { Product } from '@/types';
import { Heart, Star, ShoppingCart, Eye } from 'lucide-react';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useCart } from '@/contexts/CartContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ProductCardProps {
  product: Product;
  index?: number;
  compact?: boolean;
}

const ProductCard = ({ product, index = 0, compact = false }: ProductCardProps) => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addItem } = useCart();
  const { t, formatAmount } = useSettings();
  const navigate = useNavigate();
  const fav = isFavorite(product.id);

  const discount = product.oldPrice
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-card rounded-2xl overflow-hidden border border-border/40 premium-shadow hover:premium-shadow-hover transition-all duration-400 group"
    >
      <div
        className={`relative bg-muted/10 cursor-pointer overflow-hidden ${compact ? 'aspect-square' : 'aspect-[4/5]'}`}
        onClick={() => navigate(`/product/${product.id}`)}
      >
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          loading="lazy"
        />

        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Badge */}
        {product.badge && (
          <span className={`absolute top-2.5 start-2.5 text-[10px] font-black px-2.5 py-1 rounded-xl shadow-md ${
            product.badge === 'sale' ? 'bg-destructive text-destructive-foreground' :
            product.badge === 'new' ? 'bg-success text-success-foreground' :
            'bg-warning text-warning-foreground'
          }`}>
            {product.badge === 'sale' ? `-${discount}%` : product.badge === 'new' ? t('product.new') : t('product.hot')}
          </span>
        )}

        {/* Action buttons */}
        <div className="absolute top-2.5 end-2.5 flex flex-col gap-1.5">
          <motion.button
            whileTap={{ scale: 0.8 }}
            onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md backdrop-blur-md transition-all duration-300 ${
              fav
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-card/70 text-muted-foreground hover:text-destructive hover:bg-card/90'
            }`}
          >
            <Heart size={14} className={fav ? 'fill-current' : ''} />
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.8 }}
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}
            className="w-8 h-8 rounded-full bg-card/70 backdrop-blur-md flex items-center justify-center text-muted-foreground shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 hover:text-primary hover:bg-card/90"
          >
            <Eye size={14} />
          </motion.button>
        </div>
      </div>

      <div className="p-3">
        {/* Store Name */}
        <p className="text-[10px] text-primary font-bold tracking-wide uppercase truncate">{product.storeName}</p>

        {/* Product Name */}
        <h3
          className="text-[13px] font-bold leading-snug line-clamp-2 mt-0.5 cursor-pointer min-h-[2.5em] hover:text-primary transition-colors duration-200"
          onClick={() => navigate(`/product/${product.id}`)}
        >
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mt-1.5">
          <div className="flex items-center gap-px">
            {[1,2,3,4,5].map(star => (
              <Star
                key={star}
                size={10}
                className={`${star <= Math.round(product.rating) ? 'fill-warning text-warning' : 'text-border'}`}
              />
            ))}
          </div>
          <span className="text-[10px] text-muted-foreground font-medium">({product.reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-end justify-between mt-2.5">
          <div>
            <span className="text-[15px] font-black text-foreground tracking-tight">{formatAmount(product.price)}</span>
            {product.oldPrice && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[11px] text-muted-foreground line-through">{formatAmount(product.oldPrice)}</span>
                {discount > 0 && (
                  <span className="text-[9px] text-destructive font-black bg-destructive/10 px-1.5 py-0.5 rounded-lg">-{discount}%</span>
                )}
              </div>
            )}
          </div>
          <motion.button
            whileTap={{ scale: 0.85, rotate: -5 }}
            onClick={() => addItem(product)}
            className="w-9 h-9 rounded-xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow-primary transition-all hover:scale-105"
          >
            <ShoppingCart size={15} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
